/**
 * RevisionThread.jsx — bir proje revizyon konuşmasının salt gövdesi: ekran görüntüsü,
 * masadaki öneri (tampon) ve mesaj balonları. Saf sunum bileşeni: servis çağrısı
 * yapmaz, kendi state'i yok.
 *
 * Hem StandRevisionDetailPage.jsx'te (inceleyen kişi tarafı, Stand Listesi — yanıt
 * kutusu ve kabul/reddet aksiyonları çevresinde, bu bileşenin DIŞINDA render edilir)
 * hem de FirmaStandDetailPage.jsx/FirmaStandOverviewPage.jsx'in Modal'ında (dekoratör
 * tarafı) aynı şekilde kullanılır — bu yüzden herhangi bir "readOnly" prop'a ihtiyaç
 * yok: bileşen zaten hiçbir zaman kendi composer'ını render etmez.
 *
 * 2026-08-25 revizyonu (bkz. docs/guides/FE-Revisions-and-KVKK-update-guide.md):
 *  - `viewerRole` — hangi tarafın balonlarının sağa hizalanacağı. Aynı konuşmayı iki
 *    taraf da okuyor; "benim mesajım sağda" olmadan dekoratör kendi cümlesini karşı
 *    tarafın cümlesi gibi görüyordu.
 *  - Masadaki öneri (`revision.proposed`) ve kapanış kararı (`outcome`/`appliedValue`)
 *    konuşmanın üstünde ayrı bir kutuda — tampon konuşmanın içinde bir mesaj değil,
 *    "şu an masada olan tek değer"dir ve karar verilecek şey odur.
 */
import React from "react";
import { Badge } from "reactstrap";
import { Upload, User, Info, Inbox, Gavel } from "lucide-react";
import { REVISION_OUTCOME } from "../../services/projectService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../i18n";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(currentLocaleTag(), {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

// Beyan alanları üç tipli (NUMBER/INTEGER/BOOLEAN) ama tel üzerinde hep string —
// boolean'ı "true" diye göstermek dekoratöre hiçbir şey anlatmıyor.
export function fmtRevisionValue(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "Evet" : i18n.t("common:hayir");
  if (v === "true") return "Evet";
  if (v === "false") return i18n.t("common:hayir");
  return String(v);
}

function MessageBubble({ message, viewerRole }) {
  const { t } = useTranslation("components");
  if (message.kind === "file_upload") {
    return (
      <div className="d-flex justify-content-center my-2">
        <div className="d-inline-flex align-items-center gap-2 rounded-pill bg-light border px-3 py-1 small text-muted">
          <Upload size={13} />
          <span>{message.authorName}: {message.uploadedFileName || message.text}</span>
          <span className="text-muted">— {fmtDate(message.createdAt)}</span>
        </div>
      </div>
    );
  }

  if (message.authorRole === "system") {
    return (
      <div className="d-flex justify-content-center my-2">
        <div className="d-inline-flex align-items-center gap-2 rounded-pill bg-light border px-3 py-1 small text-muted">
          <Info size={13} />
          <span>{message.text}</span>
          <span className="text-muted">— {fmtDate(message.createdAt)}</span>
        </div>
      </div>
    );
  }

  const isMine = message.authorRole === viewerRole;
  // authorName canlı veride null gelir (backend sadece userId taşır) — rolden
  // okunabilir bir etiket türetilir.
  const label = message.authorName
    || (message.authorRole === "company" ? t("revizyon.dekorator") : "FDS");
  return (
    <div className={`d-flex flex-column my-2 ${isMine ? "align-items-end" : "align-items-start"}`}>
      <div
        className={`rounded-3 px-3 py-2 ${isMine ? "bg-primary text-white" : "bg-light border"}`}
        style={{ maxWidth: "75%" }}
      >
        <div className="d-flex align-items-center gap-1 small fw-semibold mb-1 opacity-75">
          <User size={11} />{label}
        </div>
        <div className="small" style={{ whiteSpace: "pre-wrap" }}>{message.text}</div>
      </div>
      <div className="text-muted small mt-1">{fmtDate(message.createdAt)}</div>
    </div>
  );
}

// Masadaki öneri — SPEC_FIELD revizyonlarında değer burada bekler ve kabul edilene
// kadar projede hiçbir şey değişmez. Yeni bir öneri eskisinin YERİNE geçer (geri
// çekilen değer konuşmaya düşer), o yüzden burada her zaman tek bir değer var.
function ProposalBox({ revision, fieldLabel }) {
  const { t } = useTranslation("components");
  const p = revision.proposed;
  if (!p) return null;
  return (
    <div className="border border-warning rounded p-3 mb-3 bg-light-subtle">
      <div className="d-flex align-items-center gap-2 mb-2">
        <Inbox size={15} className="text-warning" />
        <span className="fw-semibold">{t("revizyon.masadakiOneri")}</span>
        <Badge color="warning">{t("revizyon.kararBekliyor")}</Badge>
      </div>
      <div className="d-flex flex-wrap gap-4">
        <div>
          <div className="text-muted small">{fieldLabel || revision.fieldKey || t("revizyon.onerilenDeger")}</div>
          <div className="fs-5 fw-semibold">{fmtRevisionValue(p.value)}</div>
        </div>
        <div>
          <div className="text-muted small">{t("revizyon.oneriTarihi")}</div>
          <div className="small">{fmtDate(p.proposedAt)}</div>
        </div>
      </div>
      {p.note && <div className="small text-muted mt-2 fst-italic">“{p.note}”</div>}
    </div>
  );
}

// Kapanış kararı. ⚠️ outcome SADECE /accept'ten çıkar — status=resolved ile elle
// kapatılan bir revizyonda null kalır, yani "kararı kayda geçmeden kapatıldı".
function OutcomeBox({ revision }) {
  const { t } = useTranslation("components");
  if (!revision.outcome) return null;
  const meta = REVISION_OUTCOME[revision.outcome];
  return (
    <div className="border rounded p-3 mb-3">
      <div className="d-flex align-items-center gap-2">
        <Gavel size={15} className="text-muted" />
        <span className="fw-semibold">{t("revizyon.karar")}</span>
        <Badge color={meta?.color ?? "secondary"}>{meta?.label ?? revision.outcome}</Badge>
        {revision.appliedValue != null && (
          <span className="small text-muted">
            {t("revizyon.uygulananDeger")}{" "}<span className="fw-semibold">{fmtRevisionValue(revision.appliedValue)}</span>
          </span>
        )}
      </div>
    </div>
  );
}

export default function RevisionThread({ revision, viewerRole = "inspector", fieldLabel = null }) {
  const { t } = useTranslation("components");
  if (!revision) return null;

  return (
    <div>
      {revision.screenshot && (
        <div className="mb-3 text-center">
          <a href={revision.screenshot._mockUrl} target="_blank" rel="noreferrer">
            <img
              src={revision.screenshot._mockUrl}
              alt={revision.screenshot.originalFileName}
              className="img-fluid rounded border"
              style={{ maxHeight: 280 }}
            />
          </a>
          <div className="text-muted small mt-1">{revision.screenshot.originalFileName}</div>
        </div>
      )}

      <OutcomeBox revision={revision} />
      <ProposalBox revision={revision} fieldLabel={fieldLabel} />

      {revision.messages.length === 0 ? (
        <Badge color="secondary">{t("revizyon.henuzMesajYok")}</Badge>
      ) : (
        <div className="d-flex flex-column">
          {revision.messages.map((m) => <MessageBubble key={m._id} message={m} viewerRole={viewerRole} />)}
        </div>
      )}
    </div>
  );
}
